import type { LabResultRecord } from '../../types/labs';
import type { SurveillanceReviewPrompt } from '../../types/surveillance';
import { resolveBiomarker } from '../../data/biomarkerCatalogue';
import { computeBiomarkerStatus } from '../biomarkerStatus';
import { coerceDate } from '../dateLabels';
import { type SourceStatus, isUsable } from './types';

/**
 * "Your health at a glance" — four chips a member can read in five seconds.
 *
 * TWO THINGS ARE DELIBERATELY ABSENT.
 *
 * There is no composite health score. A single number implying overall health
 * is not clinically defensible without a methodology behind it, and members
 * would reasonably treat it as one.
 *
 * There is no streak. It is engagement, not health, and it has its own place in
 * the Today strip — putting a gamification counter inside a clinical row invites
 * exactly the misreading the missing score is meant to avoid.
 *
 * The biomarker chip counts three buckets: within reference range, outside it,
 * and flagged by a clinician. The first two come straight from
 * `computeBiomarkerStatus` and are purely descriptive. The third counts open
 * clinician-authored review prompts, so the wording is honest about who made
 * the call — Melio asserts no new clinical judgement of its own here.
 *
 * A chip whose source FAILED to load is omitted, never zeroed. "0 outside
 * range" produced by a permission-denied read is the single worst thing this
 * dashboard could say.
 */

export type SnapshotTone = 'neutral' | 'attention' | 'muted';

export interface SnapshotChip {
  id: 'biomarkers' | 'next_test' | 'history' | 'plan';
  label: string;
  value: string;
  hint?: string;
  tone: SnapshotTone;
  /**
   * Where the chip leads. Every chip states a fact the member will want to act
   * on, and the biomarker one in particular ("3 outside") was previously inert
   * text — we told a member they had out-of-range results and gave them
   * nothing to press. Optional so a chip can still be built without one.
   */
  href?: string;
}

export interface BiomarkerCounts {
  inRange: number;
  outOfRange: number;
  flagged: number;
  /**
   * Readings we could not place on a range — reported, never silently dropped.
   *
   * Counts BOTH readings whose value or unit can't be judged against a range,
   * and readings whose name doesn't resolve to a catalogue entry at all. The
   * second used to be skipped outright, which made the denominator lie: a
   * member with six unrecognised markers was told about the other forty-nine
   * with nothing to indicate the six existed. If we are not counting a result
   * the member can see on their uploads list, we have to say so.
   */
  notCompared: number;
}

export interface HealthSnapshotInput {
  labs: LabResultRecord[];
  labsStatus: SourceStatus;
  sex: string | null;
  openPrompts: SurveillanceReviewPrompt[];
  promptsStatus: SourceStatus;
  nextScheduledTest: { name: string; scheduledFor: Date | null } | null;
  scheduledTestsStatus: SourceStatus;
  readiness: { completionRatio: number } | null;
  intakeStatus: SourceStatus;
  activePlanItemCount: number;
  planStatus: SourceStatus;
  now?: Date;
}

/**
 * Bucket the member's biomarkers, counting each marker once at its most recent
 * reading. Without the dedupe, someone with four years of quarterly lipid panels
 * would be told they have forty biomarkers.
 */
export function countBiomarkers(
  labs: LabResultRecord[],
  sex: string | null,
  openPrompts: SurveillanceReviewPrompt[] = [],
): BiomarkerCounts {
  const latestByKey = new Map<string, { record: LabResultRecord; at: number }>();
  // Unrecognised names are still one marker each, deduped by name so repeat
  // readings of the same unknown marker don't inflate the count.
  const unresolvedNames = new Set<string>();

  for (const record of labs) {
    const meta = resolveBiomarker({ biomarkerKey: record.biomarkerKey, biomarker: record.biomarker });
    if (!meta) {
      const name = String(record.biomarker || record.biomarkerKey || '').trim().toLowerCase();
      if (name) unresolvedNames.add(name);
      continue;
    }
    const at = coerceDate(record.observedAt || record.updatedAt || record.createdAt)?.getTime() ?? 0;
    const existing = latestByKey.get(meta.key);
    if (!existing || at >= existing.at) {
      latestByKey.set(meta.key, { record, at });
    }
  }

  let inRange = 0;
  let outOfRange = 0;
  let notCompared = unresolvedNames.size;

  for (const [key, { record }] of latestByKey) {
    const meta = resolveBiomarker({ biomarkerKey: key });
    if (!meta) continue;
    const status = computeBiomarkerStatus(record, meta, sex);
    if (!status.hasBar || status.status === 'unknown') {
      notCompared += 1;
    } else if (status.status === 'in_range') {
      inRange += 1;
    } else {
      outOfRange += 1;
    }
  }

  const flagged = openPrompts.filter((prompt) => prompt.sourceType === 'blood_result').length;

  return { inRange, outOfRange, flagged, notCompared };
}

function biomarkerChip(input: HealthSnapshotInput): SnapshotChip | null {
  // A failed read must not become "0 outside range".
  if (!isUsable(input.labsStatus)) return null;

  const counts = countBiomarkers(
    input.labs,
    input.sex,
    isUsable(input.promptsStatus) ? input.openPrompts : [],
  );
  const compared = counts.inRange + counts.outOfRange;
  // Nothing comparable yet — omit the chip rather than claim a clean sweep.
  if (compared === 0) return null;

  const parts = [`${counts.inRange} within range`, `${counts.outOfRange} outside`];
  if (counts.flagged > 0) {
    parts.push(`${counts.flagged} flagged by your clinician`);
  }

  return {
    id: 'biomarkers',
    label: 'Biomarkers',
    value: parts.join(' · '),
    href: '/results',
    hint: counts.notCompared > 0
      ? `${counts.notCompared} result${counts.notCompared === 1 ? '' : 's'} we can't compare to a range`
      : undefined,
    tone: counts.flagged > 0 || counts.outOfRange > 0 ? 'attention' : 'neutral',
  };
}

function nextTestChip(input: HealthSnapshotInput): SnapshotChip | null {
  if (!isUsable(input.scheduledTestsStatus)) return null;

  const scheduledFor = input.nextScheduledTest?.scheduledFor ?? null;
  if (!scheduledFor) {
    // A plan item's free-text cadence is not a commitment to a date. Say the
    // truthful thing rather than inventing "in about 7 weeks".
    return {
      id: 'next_test',
      label: 'Next blood test',
      value: 'To be scheduled',
      tone: 'muted',
      href: '/testing',
    };
  }

  const now = input.now ?? new Date();
  const days = Math.max(0, Math.round((scheduledFor.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));
  const weeks = Math.round(days / 7);
  const value =
    days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : days < 14 ? `In ${days} days` : `In ${weeks} weeks`;

  return {
    id: 'next_test',
    label: 'Next blood test',
    value,
    hint: input.nextScheduledTest?.name,
    tone: 'neutral',
    href: '/testing',
  };
}

function historyChip(input: HealthSnapshotInput): SnapshotChip | null {
  if (!isUsable(input.intakeStatus) || !input.readiness) return null;
  const percent = Math.round(input.readiness.completionRatio * 100);
  return {
    id: 'history',
    label: 'Health history',
    value: `${percent}% complete`,
    tone: percent >= 100 ? 'neutral' : 'muted',
    href: '/history',
  };
}

function planChip(input: HealthSnapshotInput): SnapshotChip | null {
  if (!isUsable(input.planStatus)) return null;
  if (input.activePlanItemCount === 0) {
    return { id: 'plan', label: 'Your plan', value: 'Not started yet', tone: 'muted', href: '/plan' };
  }
  return {
    id: 'plan',
    label: 'Your plan',
    value: `${input.activePlanItemCount} area${input.activePlanItemCount === 1 ? '' : 's'} monitored`,
    tone: 'neutral',
    href: '/plan',
  };
}

export function buildHealthSnapshot(input: HealthSnapshotInput): SnapshotChip[] {
  return [biomarkerChip(input), nextTestChip(input), historyChip(input), planChip(input)].filter(
    (chip): chip is SnapshotChip => chip !== null,
  );
}
